import { useContext, useState } from "react";
import { Container, InputAdornment, Table, Typography } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { useShareLinksQuery, UserRoleEnum } from "../../generated/graphql";
import { UserInfoContext } from "../Main/Main";
import { ErrorMessageSnackbar } from "../shared/ErrorMessageSnackbar";
import { SuccessSnackbar } from "../shared/SuccessSnackbar";
import { SearchTextField } from "../MobileAppModal/MobileAppModal.style";
import { useDebounce } from "../shared/Utils/OtherOperations/useDebounce";
import { ShareLinksHeader } from "./components/ShareLinksHeader";
import { ShareLinksTableHeader } from "./components/ShareLinksTableHeader";
import { ShareLinksTableList } from "./components/ShareLinksTableList";
import { ShareLinkFormModal } from "./modals/ShareLinkFormModal";

const initialIsOpenModal = { isOpen: false, id: "" };

export const initialNotifications = {
  error: false,
  success: false,
  message: "",
};

export type Filters = {
  isActive: boolean | null;
};

const MANAGE_ROLES = [UserRoleEnum.Manager, UserRoleEnum.Super];

export const ShareLinks = () => {
  const user = useContext(UserInfoContext);
  const canManage = MANAGE_ROLES.includes(user?.role?.role);

  const [isOpenModal, setIsOpenModal] = useState(initialIsOpenModal);
  const [notifications, setNotifications] = useState(initialNotifications);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<Filters>({ isActive: null });
  const [searchKey, setSearchKey] = useState("");
  const debouncedSearchKey = useDebounce(searchKey.trim(), 800);

  const { data, loading } = useShareLinksQuery({
    variables: {
      filter: {
        search: debouncedSearchKey || undefined,
        isActive:
          typeof filters.isActive === "boolean" ? filters.isActive : undefined,
      },
      skip: page * rowsPerPage,
      take: rowsPerPage,
    },
    skip: !canManage,
    fetchPolicy: "cache-and-network",
  });

  const links = data?.shareLinks?.items || [];
  const total = data?.shareLinks?.total || 0;

  if (!canManage) {
    return (
      <Container maxWidth="lg">
        <Typography
          variant="subtitle1"
          color="textSecondary"
          align="center"
          style={{ margin: "60px" }}
        >
          У вас немає доступу до керування шаринг-посиланнями.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <ShareLinksHeader
        canManage={canManage}
        onOpenModal={() => setIsOpenModal({ isOpen: true, id: "" })}
      />
      <SearchTextField
        placeholder="Пошук за кодом, назвою або описом"
        value={searchKey}
        inputProps={{ maxLength: 100 }}
        onChange={({ target }) => {
          setSearchKey(target.value);
          setPage(0);
        }}
        variant="outlined"
        size="small"
        style={{ width: "540px" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />
      <Table>
        <ShareLinksTableHeader
          filters={filters}
          setFilters={setFilters}
          setPage={setPage}
        />
        <ShareLinksTableList
          loading={loading}
          links={links}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          canManage={canManage}
          onOpenModal={(id) => setIsOpenModal({ isOpen: true, id })}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          setNotifications={setNotifications}
        />
      </Table>
      {isOpenModal.isOpen && (
        <ShareLinkFormModal
          id={isOpenModal.id || undefined}
          onClose={() => setIsOpenModal(initialIsOpenModal)}
          setNotifications={setNotifications}
        />
      )}
      {notifications?.error && (
        <ErrorMessageSnackbar
          open={notifications?.error}
          closeHandler={() => setNotifications(initialNotifications)}
          message={notifications?.message}
        />
      )}
      {notifications?.success && (
        <SuccessSnackbar
          open={notifications?.success}
          handleClose={() => setNotifications(initialNotifications)}
          message={notifications?.message}
          snackbarProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          }}
        />
      )}
    </Container>
  );
};
